/**
 * The experiment registry — the code-side source of truth for experiment
 * structure (arms, priors, reward windows, cohorts). Both the webapp and the
 * refresh function import this module, so structure can never drift between
 * client and server.
 *
 * Lifecycle (active / paused / concluded) deliberately does NOT live here:
 * it is data in the per-experiment Firestore control doc, so shipping an
 * experiment's outcome is a dashboard click, not a deploy.
 *
 * Experiments are identified by `(courseId, experimentId)` and never span
 * course runs — rerunning one next year means adding a new entry under the
 * new courseId (optionally with a prior informed by the previous run) and
 * leaving the old entry in place as the permanent record.
 */

import {DEFAULT_COHORT, type BanditExperimentDefinition} from './types';
import {buildArmOverrideParam} from './urlOverride';

/**
 * Validates and returns an experiment definition. Identity function at
 * runtime apart from validation; exists so registry entries fail loudly at
 * module load (i.e. in every test run) rather than silently misbehaving.
 *
 * @param definition - The experiment to validate.
 * @return The same definition, frozen.
 * @throws {Error} If arms are missing/duplicated, the prior is non-positive,
 *   the reward window is non-positive, cohorts omit {@link DEFAULT_COHORT},
 *   or a uniform cohort is not a declared cohort.
 */
export function defineExperiment(
  definition: BanditExperimentDefinition,
): BanditExperimentDefinition {
  const label = `${definition.courseId}/${definition.experimentId}`;
  if (definition.arms.length < 2) {
    throw new Error(`Experiment ${label} needs at least two arms`);
  }
  if (new Set(definition.arms).size !== definition.arms.length) {
    throw new Error(`Experiment ${label} has duplicate arm names`);
  }
  if (definition.prior.alpha <= 0 || definition.prior.beta <= 0) {
    throw new Error(`Experiment ${label} prior must be strictly positive`);
  }
  if (definition.rewardWindowHours <= 0) {
    throw new Error(`Experiment ${label} reward window must be positive`);
  }
  if (!definition.cohorts.includes(DEFAULT_COHORT)) {
    throw new Error(
      `Experiment ${label} cohorts must include '${DEFAULT_COHORT}'`,
    );
  }
  if (new Set(definition.cohorts).size !== definition.cohorts.length) {
    throw new Error(`Experiment ${label} has duplicate cohort names`);
  }
  for (const cohort of definition.uniformCohorts ?? []) {
    if (!definition.cohorts.includes(cohort)) {
      throw new Error(
        `Experiment ${label} uniform cohort '${cohort}' is not declared`,
      );
    }
  }
  const floor = definition.minArmProbability;
  if (
    floor !== undefined &&
    (!(floor > 0) || floor > 1 / definition.arms.length)
  ) {
    throw new Error(
      `Experiment ${label} minArmProbability must be in (0, 1 / arms]`,
    );
  }
  return Object.freeze(definition);
}

// ── Experiment: math IDE Solve-stage layout ──────────────────────────────────

/** Registry ID of the math IDE layout experiment. */
export const MATHIDE_LAYOUT_EXPERIMENT_ID = 'mathide-layout';

/**
 * Arms of the math IDE layout experiment:
 * - `agent-center`: coding agent in the center column, code + results on the
 *   right (code column collapsible).
 * - `code-center`: code + results in the center, agent chat on the right
 *   (chat collapsible).
 * - `classic-ide`: code-centered with the agent hidden until the student
 *   intentionally opens it via the AI Help affordance.
 */
export const MATHIDE_LAYOUT_ARMS = [
  'agent-center',
  'code-center',
  'classic-ide',
] as const;

/** One arm of {@link MATHIDE_LAYOUT_ARMS}. */
export type MathIdeLayoutArm = (typeof MATHIDE_LAYOUT_ARMS)[number];

/** Course run the math IDE layout experiment targets. */
const MATHIDE_LAYOUT_COURSE_ID = 'pai1';

/**
 * Assignment used for dashboard preview links. Any real assignment in the
 * course works; the override only affects layout, not content.
 */
const MATHIDE_LAYOUT_PREVIEW_ASSIGNMENT = 'ai-text-detect';

// ── Experiment: peervote social flow ─────────────────────────────────────────

/** Registry ID of the peervote social-flow experiment. */
export const PEERVOTE_SOCIAL_EXPERIMENT_ID = 'peervote-social';

/**
 * Arms of the peervote social-flow experiment:
 * - `answer-only`: the student answers the (graded) question and moves
 *   on — no compare/results steps.
 * - `peer-experience`: the full invite-mode flow — answer, compare a
 *   ballot of answers, see class results, optionally publish (#3738).
 */
export const PEERVOTE_SOCIAL_ARMS = ['answer-only', 'peer-experience'] as const;

/** One arm of {@link PEERVOTE_SOCIAL_ARMS}. */
export type PeerVoteSocialArm = (typeof PEERVOTE_SOCIAL_ARMS)[number];

/** Course run the peervote social-flow experiment targets. */
const PEERVOTE_SOCIAL_COURSE_ID = 'pai1';

// ── Experiments: teaching puzzles levers ─────────────────────────────────────

/** Pace: muted after each student line until the SL talks, or an open mic. */
export const PUZZLE_PACE_EXPERIMENT_ID = 'puzzle-pace';
/** Arms of {@link PUZZLE_PACE_EXPERIMENT_ID}. */
export const PUZZLE_PACE_ARMS = ['pause', 'open-mic'] as const;

/** Legibility: the card engine that says plainly when a move hasn't landed. */
export const PUZZLE_LEGIBILITY_EXPERIMENT_ID = 'puzzle-legibility';
/** Arms of {@link PUZZLE_LEGIBILITY_EXPERIMENT_ID}: `cards-10g` and `cards-10gc`. */
export const PUZZLE_LEGIBILITY_ARMS = ['gentle', 'candid'] as const;

/** Support: whether the debrief challenge offers its hint. */
export const PUZZLE_SUPPORT_EXPERIMENT_ID = 'puzzle-support';
/** Arms of {@link PUZZLE_SUPPORT_EXPERIMENT_ID}. */
export const PUZZLE_SUPPORT_ARMS = ['hint', 'no-hint'] as const;

/**
 * The least chance each arm of {@link PUZZLE_SUPPORT_EXPERIMENT_ID} has in the
 * Thompson-sampled cohort.
 */
export const PUZZLE_SUPPORT_MIN_ARM_PROBABILITY = 0.2;

/** The teaching-puzzles holdout cohort, assigned by an even coin flip. */
export const PUZZLE_UNIFORM_COHORT = 'uniform';

/** Courses whose teaching puzzles run the lever experiments. */
const PUZZLE_EXPERIMENT_COURSE_IDS = ['cipx', 'pai1'] as const;

/**
 * Percent of a course's SLs in {@link PUZZLE_UNIFORM_COHORT}, chosen by
 * {@link holdoutBucket} so they are uniform in all three experiments. cipx
 * runs them as a plain A/B test; pai1 Thompson-samples beside a holdout.
 */
const PUZZLE_HOLDOUT_PERCENT_BY_COURSE: Readonly<
  Record<(typeof PUZZLE_EXPERIMENT_COURSE_IDS)[number], number>
> = {
  cipx: 100,
  pai1: 20,
};

/**
 * Percent of a course's SLs who get an even coin flip in the teaching-puzzles
 * lever experiments.
 *
 * @param courseId - The course.
 * @return The percent; 0 for a course that doesn't run them.
 * @throws Never.
 */
export function puzzleHoldoutPercent(courseId: string): number {
  return (
    Object.entries(PUZZLE_HOLDOUT_PERCENT_BY_COURSE).find(
      ([id]) => id === courseId,
    )?.[1] ?? 0
  );
}

function definePuzzleExperiments(
  courseId: string,
): BanditExperimentDefinition[] {
  const levers: ReadonlyArray<{
    experimentId: string;
    arms: readonly string[];
    description: string;
    minArmProbability?: number;
  }> = [
    {
      experimentId: PUZZLE_PACE_EXPERIMENT_ID,
      arms: PUZZLE_PACE_ARMS,
      description:
        'Teaching puzzles Pace: muted after each student line (pause) vs an open mic.',
    },
    {
      experimentId: PUZZLE_LEGIBILITY_EXPERIMENT_ID,
      arms: PUZZLE_LEGIBILITY_ARMS,
      description:
        'Teaching puzzles Legibility: the student goes along gently (cards-10g) vs says plainly when a move has not landed (cards-10gc).',
    },
    {
      experimentId: PUZZLE_SUPPORT_EXPERIMENT_ID,
      arms: PUZZLE_SUPPORT_ARMS,
      description:
        'Teaching puzzles Support: the debrief challenge offers a hint vs no hint.',
      // The reward is a return visit, which a hint may not move; keep both
      // arms in play so challenge completion can still be compared.
      minArmProbability: PUZZLE_SUPPORT_MIN_ARM_PROBABILITY,
    },
  ];
  const holdoutPercent = puzzleHoldoutPercent(courseId);
  const allocation =
    holdoutPercent >= 100
      ? 'Every SL gets an even coin flip (an A/B test).'
      : `${String(holdoutPercent)}% of SLs are a uniform holdout.`;
  return levers.map(({experimentId, arms, description, minArmProbability}) =>
    defineExperiment({
      courseId,
      experimentId,
      description:
        description +
        ' Reward: starts another puzzle session within the window after the first. ' +
        allocation +
        (minArmProbability !== undefined && holdoutPercent < 100
          ? ` Each arm keeps at least ${String(minArmProbability * 100)}% of the Thompson-sampled SLs.`
          : ''),
      arms,
      prior: {alpha: 1, beta: 1},
      // Launch traffic is a burst: the reward must land within the visit.
      rewardWindowHours: 2,
      cohorts: [DEFAULT_COHORT, PUZZLE_UNIFORM_COHORT],
      uniformCohorts: [PUZZLE_UNIFORM_COHORT],
      ...(minArmProbability !== undefined ? {minArmProbability} : {}),
      previewPath: arm =>
        `/${courseId}/teaching-puzzles?${buildArmOverrideParam({experimentId, arm})}`,
    }),
  );
}

// ── Experiment: PAI section practice students ────────────────────────────────

/** Section practice: who sits in the room, and whether the SL sees why. */
export const PRACTICE_STUDENTS_EXPERIMENT_ID = 'practice-students';

/**
 * Arms of {@link PRACTICE_STUDENTS_EXPERIMENT_ID}. Every arm shows the
 * "Students like yours" card:
 * - `regulars`: the course's default students, unseeded.
 * - `seeded`: seats seeded from the section's practice seeds.
 * - `seeded-visible`: seeded, and the green room lists the seeded
 *   misconceptions.
 */
export const PRACTICE_STUDENTS_ARMS = [
  'regulars',
  'seeded',
  'seeded-visible',
] as const;

/** One arm of {@link PRACTICE_STUDENTS_ARMS}. */
export type PracticeStudentsArm = (typeof PRACTICE_STUDENTS_ARMS)[number];

/** Course run the section practice students experiment targets. */
const PRACTICE_STUDENTS_COURSE_ID = 'pai1';

// ── The registry ──────────────────────────────────────────────────────────────

/**
 * All experiments, past and present. Append-only by convention: concluded
 * experiments stay listed so their dashboards remain renderable and their
 * IDs are never accidentally reused.
 */
export const BANDIT_EXPERIMENTS: readonly BanditExperimentDefinition[] = [
  defineExperiment({
    courseId: MATHIDE_LAYOUT_COURSE_ID,
    experimentId: MATHIDE_LAYOUT_EXPERIMENT_ID,
    description:
      'Math IDE Solve-stage layout: agent-centered vs code-centered vs ' +
      'classic IDE with intentional AI. Reward: passed Check Correct within ' +
      'the window after first exposure.',
    arms: MATHIDE_LAYOUT_ARMS,
    prior: {alpha: 1, beta: 1},
    rewardWindowHours: 6,
    cohorts: [DEFAULT_COHORT],
    previewPath: arm =>
      `/${MATHIDE_LAYOUT_COURSE_ID}/mide/a/${MATHIDE_LAYOUT_PREVIEW_ASSIGNMENT}` +
      `?${buildArmOverrideParam({experimentId: MATHIDE_LAYOUT_EXPERIMENT_ID, arm})}`,
  }),
  defineExperiment({
    courseId: PEERVOTE_SOCIAL_COURSE_ID,
    experimentId: PEERVOTE_SOCIAL_EXPERIMENT_ID,
    description:
      'Peer Vote slide: answer-only vs the full compare/results peer ' +
      'experience (#3738). Reward: application reaches complete within ' +
      'the window after first exposure to a peervote slide.',
    arms: PEERVOTE_SOCIAL_ARMS,
    prior: {alpha: 1, beta: 1},
    // Finishing the application is a days-scale outcome, unlike the math
    // IDE's same-session solve — hence the week-long window.
    rewardWindowHours: 168,
    cohorts: [DEFAULT_COHORT],
    // No previewPath: peervote slides live inside authored lessons, so
    // there is no stable URL to preview against; use `?arm=` manually on
    // whichever lesson hosts the slide.
  }),
  ...PUZZLE_EXPERIMENT_COURSE_IDS.flatMap(definePuzzleExperiments),
  defineExperiment({
    courseId: PRACTICE_STUDENTS_COURSE_ID,
    experimentId: PRACTICE_STUDENTS_EXPERIMENT_ID,
    description:
      'PAI section practice: the regular students vs students seeded from ' +
      "the SL's section vs seeded with the misconceptions shown. Every arm " +
      'shows the same card; an even split. Reward: starts another practice ' +
      'session within the window after the first.',
    arms: PRACTICE_STUDENTS_ARMS,
    prior: {alpha: 1, beta: 1},
    rewardWindowHours: 168,
    cohorts: [DEFAULT_COHORT],
    uniformCohorts: [DEFAULT_COHORT],
    previewPath: arm =>
      `/${PRACTICE_STUDENTS_COURSE_ID}/section-practice?${buildArmOverrideParam({experimentId: PRACTICE_STUDENTS_EXPERIMENT_ID, arm})}`,
  }),
];

/**
 * Experiments registered for one course run.
 *
 * @param courseId - Course to look up.
 * @return Possibly-empty list of definitions (never null).
 */
export function getExperimentsForCourse(
  courseId: string,
): readonly BanditExperimentDefinition[] {
  return BANDIT_EXPERIMENTS.filter(e => e.courseId === courseId);
}

/**
 * Looks up one experiment by identity.
 *
 * @param options.courseId - Course run.
 * @param options.experimentId - Experiment ID within the course.
 * @return The definition, or undefined when not registered.
 */
export function getBanditExperiment({
  courseId,
  experimentId,
}: {
  courseId: string;
  experimentId: string;
}): BanditExperimentDefinition | undefined {
  return BANDIT_EXPERIMENTS.find(
    e => e.courseId === courseId && e.experimentId === experimentId,
  );
}
